<template>
  <el-dialog
    title="添加会员"
    :visible.sync="dialogVisible"
    width="520px"
    append-to-body
    :close-on-click-modal="false"
    @closed="resetForm"
  >
    <el-form ref="createForm" :model="form" :rules="rules" label-width="120px">
      <el-form-item label="平台名称：" prop="appId">
        <el-select
          v-model="form.appId"
          filterable
          remote
          clearable
          :remote-method="fetchAppList"
          :loading="appLoading"
          placeholder="请输入平台名称搜索"
          style="width: 100%"
        >
          <el-option
            v-for="item in appList"
            :key="item.id"
            :label="item.name"
            :value="item.id"
          ></el-option>
        </el-select>
      </el-form-item>

      <el-form-item label="渠道：" prop="channelId">
        <el-select
          v-model="form.channelId"
          filterable
          remote
          clearable
          :remote-method="filterChannelList"
          :loading="channelLoading"
          placeholder="请输入渠道名称搜索"
          style="width: 100%"
          :disabled="!form.appId"
        >
          <el-option
            v-for="item in filteredChannelList"
            :key="item.id"
            :label="channelLabel(item)"
            :value="item.id"
          ></el-option>
        </el-select>
      </el-form-item>

      <el-form-item
        label="用户名："
        prop="username"
        :error="accountError"
        :class="{ 'account-required': !form.mobile }"
      >
        <el-input
          v-model="form.username"
          placeholder="请输入用户名"
          maxlength="64"
          @input="handleAccountInput"
        ></el-input>
      </el-form-item>

      <el-form-item
        ref="mobileFormItem"
        label="手机号："
        prop="mobile"
        :class="{ 'account-required': !form.username }"
      >
        <el-input
          v-model="form.mobile"
          placeholder="请输入手机号"
          maxlength="11"
          @input="handleAccountInput"
        ></el-input>
      </el-form-item>

      <el-form-item label="登录密码：" prop="password">
        <el-input
          v-model="form.password"
          type="password"
          placeholder="请输入至少6位登录密码"
          maxlength="64"
          show-password
          autocomplete="new-password"
        ></el-input>
      </el-form-item>

      <div class="form-tip">
        用户名和手机号至少填写一个，支付密码默认与登录密码一致
      </div>
    </el-form>

    <span slot="footer" class="dialog-footer">
      <el-button @click="dialogVisible = false">取消</el-button>
      <el-button type="primary" :loading="submitting" @click="handleSubmit">
        保存
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
import { createUser, getAppList, getChannelList } from "@/api/member";

export default {
  name: "MemberCreateUser",
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    const validateMobile = (rule, value, callback) => {
      if (!value) {
        callback();
        return;
      }

      const regPhone = /^1[3-9]\d{9}$/;
      if (!regPhone.test(value)) {
        callback(new Error("请输入正确的手机号"));
        return;
      }

      callback();
    };

    return {
      submitting: false,
      accountError: "",
      appLoading: false,
      channelLoading: false,
      form: {
        appId: "",
        channelId: "",
        username: "",
        mobile: "",
        password: ""
      },
      rules: {
        appId: [{ required: true, message: "请选择平台", trigger: "change" }],
        channelId: [
          { required: true, message: "请选择渠道", trigger: "change" }
        ],
        mobile: [{ validator: validateMobile, trigger: "blur" }],
        password: [
          { required: true, message: "请输入登录密码", trigger: "blur" },
          { min: 6, message: "登录密码不能少于6位", trigger: "blur" }
        ]
      },
      appList: [],
      channelList: [],
      filteredChannelList: []
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit("update:visible", value);
      }
    }
  },
  watch: {
    visible(value) {
      if (value) {
        this.fetchAppList();
      }
    },
    "form.appId"(newVal) {
      this.form.channelId = "";
      this.channelList = [];
      this.filteredChannelList = [];
      if (newVal) {
        this.fetchChannelList(newVal);
      }
    }
  },
  methods: {
    // 获取平台列表（支持模糊搜索）
    async fetchAppList(query) {
      this.appLoading = true;
      try {
        const params = query ? { name: query } : {};
        const res = await getAppList(params);
        if (res && res.err_code === 0) {
          this.appList = res.rows || [];
        }
      } catch (error) {
        console.error("获取平台列表失败:", error);
      } finally {
        this.appLoading = false;
      }
    },

    // 获取渠道列表
    async fetchChannelList(appId) {
      this.channelLoading = true;
      try {
        const res = await getChannelList({ app_id: appId });
        if (res && res.err_code === 0) {
          this.channelList = res.rows || [];
          this.filteredChannelList = [...this.channelList];
        }
      } catch (error) {
        console.error("获取渠道列表失败:", error);
      } finally {
        this.channelLoading = false;
      }
    },

    // 过滤渠道列表（支持模糊搜索）
    filterChannelList(query) {
      const keyword = String(query || "")
        .trim()
        .toLowerCase();
      this.filteredChannelList = keyword
        ? this.channelList.filter(item =>
            this.channelLabel(item)
              .toLowerCase()
              .includes(keyword)
          )
        : [...this.channelList];
    },
    channelLabel(item) {
      const name = item.realname || item.name || "";
      return `${name}${item.alias ? ` (${item.alias})` : ""}`;
    },
    handleAccountInput() {
      this.accountError = "";
      this.$nextTick(() => {
        if (this.$refs.mobileFormItem) {
          this.$refs.mobileFormItem.clearValidate();
        }
      });
    },
    resetForm() {
      this.form = {
        appId: "",
        channelId: "",
        username: "",
        mobile: "",
        password: ""
      };
      this.channelList = [];
      this.filteredChannelList = [];
      this.accountError = "";
      this.$nextTick(() => {
        if (this.$refs.createForm) this.$refs.createForm.clearValidate();
      });
      this.$emit("close");
    },
    handleSubmit() {
      const accountValid = Boolean(this.form.username || this.form.mobile);
      this.accountError = accountValid ? "" : "用户名和手机号至少填写一个";
      this.$refs.createForm.validate(async valid => {
        if (!valid || !accountValid) return;

        this.submitting = true;
        try {
          const params = {
            app_id: Number(this.form.appId),
            channel_id: Number(this.form.channelId),
            username: this.form.username,
            mobile: this.form.mobile,
            password: this.form.password
          };

          const res = await createUser(params);
          if (res && res.err_code === 0) {
            this.$message.success("会员添加成功");
            this.$emit("success");
            this.dialogVisible = false;
          } else {
            this.$message.error((res && res.err_msg) || "会员添加失败");
          }
        } catch (error) {
          console.error("添加会员失败:", error);
          this.$message.error("会员添加失败");
        } finally {
          this.submitting = false;
        }
      });
    }
  }
};
</script>

<style lang="less" scoped>
.dialog-footer {
  text-align: center;
}
.form-tip {
  margin-left: 120px;
  color: #f56c6c;
  font-size: 13px;
  line-height: 20px;
}
.account-required /deep/ .el-form-item__label::before {
  content: "*";
  margin-right: 4px;
  color: #f56c6c;
}
</style>
